<template>
	<section
		class="scene-user-profile"
		v-bind:class="{
			'state-has-avatar': userModel.avatar?.id,
		}"
	>
		<section class="profile-photo">
			<figure>
				<img v-bind:src="userAvatarUrl" class="user-photo" />
			</figure>
		</section>

		<header class="primary-header">
			<div class="user-info">
				<h3 class="name">{{ userModel.getName() }}</h3>
				<p class="caption">{{ userModel.getLastSeenForDisplay() }}</p>
			</div>

			<div class="favorite">
				<!-- <ChalkyReactionFavorite class="pull-right" v-bind:model="userModel" /> -->
			</div>
		</header>

		<section class="biography">
			<p class="type-large-paragraph" v-html="userBiography"></p>
		</section>

		<section class="games-played">
			<ChalkyUserFormattedGames v-bind:userModel="userModel" />
		</section>

		<section class="favorite-places">
			<ViewUserFavoritePlaces
				v-bind:favoriteCollection="favoriteCollection"
				v-bind:showViewAll="false"
				v-bind:userModel="userModel"
			/>
		</section>

		<!--
			This needs to be worked on but I commented it out so we don't
			accidentally ship it
		-->
		<!-- <section class="gallery">
			<section
				v-for="video in userModel.media.videos"
				v-bind:key="video.id"
				class="media-item padded rounded"
			>
				<ChalkyUtilityVideoNative
					v-bind:allowControl="true"
					v-bind:autoplay="false"
					v-bind:muted="false"
					v-bind:url="video.getUrl()"
				/>
			</section>
		</section> -->

		<section class="stats">
			<ChalkyUiStatisticSmall label="Total Checkins" v-bind:value="userModel.getTotalCheckins()" />
			<ChalkyUiStatisticSmall label="Places Visited" v-bind:value="userModel.getUniquePlacesVisited()" />
			<ChalkyUiStatisticSmall label="Contributions" v-bind:value="userModel.getTotalContributions()" />
		</section>

		<section class="play-frequency-chart">
			<ChalkyUiStatisticBar label="S" v-bind:value="getCheckinBarValueCentered('Sunday')" />
			<ChalkyUiStatisticBar label="M" v-bind:value="getCheckinBarValueCentered('Monday')" />
			<ChalkyUiStatisticBar label="T" v-bind:value="getCheckinBarValueCentered('Tuesday')" />
			<ChalkyUiStatisticBar label="W" v-bind:value="getCheckinBarValueCentered('Wednesday')" />
			<ChalkyUiStatisticBar label="T" v-bind:value="getCheckinBarValueCentered('Thursday')" />
			<ChalkyUiStatisticBar label="F" v-bind:value="getCheckinBarValueCentered('Friday')" />
			<ChalkyUiStatisticBar label="S" v-bind:value="getCheckinBarValueCentered('Saturday')" />
		</section>

		<section class="play-frequency-text" v-if="userModel.getMostFrequentCheckinDay()">
			<p>You can usually find me tearing up the felt on</p>
			<h3>{{ userModel.getMostFrequentCheckinDay() }}</h3>
		</section>

		<section class="recent-checkins">
			<ViewUserRecentCheckins
				v-bind:userModel="userModel"
				v-bind:venueCheckinCollection="venueCheckinCollection"
			/>
		</section>
	</section>
</template>

<script lang="ts">
	import * as Config from '@/Config';
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { assetRef } from '@/Manager/Asset';

	/**
	 * User profile scene displaying avatar, biography, gallery,
	 * statistics, play frequency chart, and recent checkins
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/User
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneUserProfile extends SceneBase {
		/**
		 * Default profile image URL based on user ID.
		 * Returns a different default image for each user based on their ID.
		 *
		 * @return string
		 */
		protected get defaultProfileImageUrl(): string {
			const id = parseInt(this.userModel.id.toString() || '1');
			const defaultImageCount = Config.App.DEFAULT_USER_PHOTO_COUNT;
			const imageIndex = id % defaultImageCount;
			const imageName = `default-photo-${imageIndex.toString().padStart(2, '0')}.jpg`;
			const imagePath = `image/user/${imageName}`;

			return assetRef(imagePath);
		}

		/**
		 * Default taunt message based on user ID.
		 * Returns a different taunt for each user based on their ID.
		 *
		 * @return string
		 */
		protected get defaultTaunt(): string {
			const taunts = ChalkySticks.Core.Data.Taunts;
			const tauntCount = taunts.length;
			const tauntIndex = parseInt(this.userModel.id.toString() || '1') % tauntCount;

			return taunts[tauntIndex] || 'You can call your shot, but it won&rsquo;t matter.';
		}

		/**
		 * Returns the URL of the user's avatar or a default image if not set.
		 *
		 * @return string
		 */
		protected get userAvatarUrl(): string {
			return this.userModel.getBestAvatar()?.getUrlMedium() || this.defaultProfileImageUrl;
		}

		/**
		 * Returns the user's biography or a default taunt.
		 *
		 * @return string
		 */
		protected get userBiography(): string {
			return this.userModel.getBiography() || this.defaultTaunt;
		}

		/**
		 * Collection of user's favorite venues
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		@Prop()
		public favoriteCollection!: ChalkySticks.Collection.Venue;

		/**
		 * The user model to display
		 *
		 * @type ChalkySticks.Model.User
		 */
		@Prop()
		public userModel!: ChalkySticks.Model.User;

		/**
		 * Collection of user's recent venue checkins
		 *
		 * @type ChalkySticks.Collection.VenueCheckin
		 */
		@Prop()
		public venueCheckinCollection!: ChalkySticks.Collection.VenueCheckin;

		/**
		 * Safely get the normalized checkin bar value (0-1) for a given day.
		 *
		 * @param string day
		 * @return number
		 */
		protected getCheckinBarValue(day: string): number {
			const percentages: Record<string, number> = this.userModel.getCheckinsByDayPercentages();
			const percentage: number = percentages[day] || 0;

			return typeof percentage === 'number' && !isNaN(percentage) ? percentage / 100 : 0;
		}

		/**
		 * Get the normalized checkin bar value centered around the average.
		 * Values above average go positive, below average go negative.
		 *
		 * @param string day
		 * @return number
		 */
		protected getCheckinBarValueCentered(day: string): number {
			const percentages: Record<string, number> = this.userModel.getCheckinsByDayPercentages();
			const percentage: number = percentages[day] || 0;

			// Calculate the average across all days
			const allValues: number[] = Object.values(percentages);
			const average: number = allValues.reduce((sum: number, val: number) => sum + val, 0) / allValues.length;

			// Get deviation from average
			const deviation: number = percentage - average;

			// Normalize the deviation to -1 to 1 range
			const maxExpectedDeviation: number = 50;
			const normalizedDeviation: number = Math.max(-1, Math.min(1, deviation / maxExpectedDeviation));

			return normalizedDeviation + 0.5;
		}
	}
</script>

<style lang="scss">
	.scene-user-profile {
		.profile-photo {
			height: 350px;
			margin-bottom: -100px;
			position: relative;
			width: 100%;

			> figure {
				-webkit-mask: linear-gradient(to bottom, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 100%);
				height: 100%;
				margin: 0;
				mask: linear-gradient(to bottom, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 100%);
				opacity: 0.5;
				overflow: hidden;
				position: relative;

				> img {
					height: 100%;
					object-fit: cover;
					width: 100%;
				}
			}
		}

		> .primary-header {
			align-items: center;
			display: grid;
			gap: 0.5rem;
			grid-template-columns: 1fr 60px;
			padding: 1rem;
			position: relative;

			.user-info .name {
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				display: -webkit-box;
				line-height: 1;
				margin-bottom: 0.25rem;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.user-info .caption {
				color: var(--chalky-grey-2);
				overflow: hidden;
				text-overflow: ellipsis;
				text-transform: capitalize;
			}
		}

		.feed-item {
			background-color: var(--chalky-blue-3);
			position: relative;

			.view-module-header {
				margin-bottom: 0;
				padding: var(--padding);
			}

			.body {
				height: 100%;
				overflow: hidden;
				position: relative;
			}
		}

		.biography {
			margin: 0 auto;
			max-width: 380px;
			padding: 1rem 3rem;
			text-align: center;
			text-wrap: pretty;
		}

		.games-played {
			padding: 1rem;
		}

		.stats {
			display: grid;
			gap: 0.5rem;
			grid-template-columns: repeat(3, 1fr);
			margin: 2rem auto 0 auto;
			margin-bottom: 1rem;
			max-width: 400px;
			padding: 1rem 0rem 1rem 2rem;
		}

		.play-frequency-chart {
			display: grid;
			gap: 0.5rem;
			grid-template-columns: repeat(7, 1fr);
			margin: 0 auto;
			margin-bottom: 1rem;
			max-width: 400px;
			padding: 1rem;

			.chalky.ui-bargraph {
				--chalky-bargraph-container-size-x: 24px;
			}
		}

		.play-frequency-text {
			margin-bottom: 2rem;
			padding: 1rem;
			text-align: center;

			p {
				color: var(--chalky-grey-2);
				margin-bottom: 0;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.scene-user-profile.state-has-avatar {
		.profile-photo {
			> figure {
				opacity: 0.9;
			}
		}
	}
</style>
